import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { endOfDay, format, startOfDay } from 'date-fns';
import { html, nothing } from 'lit';
import { guard } from 'lit/directives/guard.js';
import { setBadContactControlled } from '../../api/clienthelper.js';
import { BadcontactEntity } from '../../entities/badcontact.js';

export const BadcontactDashboard = stateful(() => {
  // Use useState for the selected conversation and filters
  const [searchText, setSearchText] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [hasTicketcountState, setHasTicketcountState] = useState<number>(1000);
  const [isSanitizedFilter, setIsSanitizedFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [isCheckedBySupportFilter, setIsCheckedBySupportFilter] = useState<'all' | 'yes' | 'no'>('all');

  // Function to clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setHasTicketcountState(1000);
    setIsSanitizedFilter('all');
    setIsCheckedBySupportFilter('all');
  };

  const conversationDataviewOptions = useMemo(() => {
    const filterAspects: any[] = [];

    if (hasTicketcountState < 1000) {
      filterAspects.push(new IntAspect({ fieldName: 'ticketCount', min: hasTicketcountState }));
    }

    // Add date range filter - if both dates are provided, use range mode
    if (startDate && endDate) {
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          secondValue: endOfDay(new Date(endDate)),
          mode: 'range',
        }),
      );
    } else if (startDate) {
      // Only start date - use after mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          firstValue: startOfDay(new Date(startDate)),
          mode: 'after',
        }),
      );
    } else if (endDate) {
      // Only end date - use before mode
      filterAspects.push(
        new DateTimeAspect({
          fieldName: 'createdAt',
          secondValue: endOfDay(new Date(endDate)),
          mode: 'before',
        }),
      );
    }

    // Add isSanitized filter
    if (isSanitizedFilter === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isSanitized',
          value: true,
        }),
      );
    } else if (isSanitizedFilter === 'no') {
      // For "not sanitized", we want to include both false and null values
      // Use StringAspect with empty mode to catch all non-true values (null, false, undefined)
      filterAspects.push(
        new StringAspect({
          fieldName: 'isSanitized',
          mode: 'empty',
        }),
      );
    }

    // Add isCheckedBySupport filter
    if (isCheckedBySupportFilter === 'yes') {
      filterAspects.push(
        new BooleanAspect({
          fieldName: 'isCheckedBySupport',
          value: true,
        }),
      );
    } else if (isCheckedBySupportFilter === 'no') {
      // For "not checked by support", we want to include both false and null values
      // Use StringAspect with empty mode to catch all non-true values (null, false, undefined)
      filterAspects.push(
        new StringAspect({
          fieldName: 'isCheckedBySupport',
          mode: 'empty',
        }),
      );
    }

    const handleSetBadContactControlled = async (contact: BadcontactEntity) => {
      await setBadContactControlled(contact._id)();
    };

    return [
      buildFullOptions<typeof BadcontactEntity>({
        class: BadcontactEntity,
        customColumns: [
          {
            fieldName: 'odooContactID',
            columnName: 'Odoo Contact ID',
            columnWidth: '140px',
            renderItem: value => html` ${value.odooContactID || 'N/A'} `,
          },
          {
            fieldName: 'displayName',
            columnName: 'Name',
            // columnWidth: '250px',
            renderItem: value => html` ${value.displayName || 'N/A'} `,
          },
          {
            fieldName: 'badContactCategory',
            columnName: 'Error-Category',
            columnWidth: '250px',
            renderItem: value => html` ${value.badContactCategory || 'N/A'} `,
          },
          {
            fieldName: 'ticketCount',
            columnName: 'Ticket Count',
            columnWidth: '100px',
            renderItem: value => html` ${value.ticketCount ?? 'N/A'} `,
          },
          {
            fieldName: 'createdAt',
            columnName: 'Created At',
            columnWidth: '200px',
            renderItem: value => format(value.createdAt ?? new Date(), 'Pp'),
          },
          {
            fieldName: 'createdDateOdoo',
            columnName: 'Created Date Odoo',
            columnWidth: '200px',
            renderItem: value => format(value.createdDateOdoo ?? new Date(), 'Pp'),
          },
          {
            fieldName: 'isSanitized',
            columnName: 'Sanitized (deleted/ fixed)',
            columnWidth: '100px',
            renderItem: value =>
              value.isSanitized
                ? html`<span class="text-secondary">✅</span>`
                : html`<span class="text-error">❌</span>`,
          },
          {
            fieldName: 'isCheckedBySupport',
            columnName: 'Checked by Support',
            columnWidth: '100px',
            renderItem: value =>
              value.isCheckedBySupport
                ? html`<span @click=${() => handleSetBadContactControlled(value)} class="text-secondary">✅</span>`
                : html`<span @click=${() => handleSetBadContactControlled(value)} class="text-error">❌</span>`,
          },
        ],
        isSortable: false,
        view: new View({
          filter: new AdornisFilter({
            searchFields: ['odooContactID', 'displayName', 'badContactCategory'],
            search: searchText,
            aspects: filterAspects,
          }),
        }),
      }),
    ];
  }, [searchText, startDate, endDate, hasTicketcountState, isSanitizedFilter, isCheckedBySupportFilter]);

  return html`
    <div class="flex gap-4 h-full">
      <div class="flex-1 card bg-base-100 shadow-xl border border-base-300 overflow-auto w-full">
        <div class="card-body">
          <div class="flex justify-between items-center mb-2 sticky top-0 bg-base-100 z-10 py-4">
            <h2 class="card-title text-2xl font-bold text-secondary">Incorrect Person Contacts</h2>

            <div class="flex items-center gap-2">
              <button class="btn btn-sm btn-outline btn-secondary" @click=${() => setShowFilters(!showFilters)}>
                ${showFilters ? 'Hide Filters' : 'Show Filters'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"
                  />
                </svg>
              </button>
            </div>
          </div>

          ${showFilters
            ? html`
                <div class="bg-base-200 p-4 rounded-lg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    <!-- Text Search -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Text Search</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${searchText}
                        @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
                      />
                    </div>

                    <!-- Date Range - Combined in one container to keep them together -->
                    <div class="form-control col-span-1 md:col-span-2">
                      <label class="label">
                        <span class="label-text font-medium">Date Range</span>
                      </label>
                      <div class="flex gap-2">
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="Start date"
                            class="input input-bordered input-sm w-full"
                            .value=${startDate}
                            @input=${(e: InputEvent) => setStartDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="date"
                            placeholder="End date"
                            class="input input-bordered input-sm w-full"
                            .value=${endDate}
                            @input=${(e: InputEvent) => setEndDate((e.target as HTMLInputElement).value)}
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Filter Ticket Count-->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Ticket Count</span>
                      </label>
                      <input
                        type="number"
                        placeholder="Search in conversations..."
                        class="input input-bordered input-sm w-full"
                        .value=${hasTicketcountState}
                        @input=${(e: InputEvent) =>
                          setHasTicketcountState(Number((e.target as HTMLInputElement).value))}
                      />
                    </div>

                    <!-- Is Sanitized Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Is Sanitized</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${isSanitizedFilter}
                        @change=${(e: InputEvent) =>
                          setIsSanitizedFilter((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Sanitized</option>
                        <option value="no">Not Sanitized</option>
                      </select>
                    </div>

                    <!-- Is Checked by Support Filter -->
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text font-medium">Checked by Support</span>
                      </label>
                      <select
                        class="select select-sm select-bordered w-full"
                        .value=${isCheckedBySupportFilter}
                        @change=${(e: InputEvent) =>
                          setIsCheckedBySupportFilter((e.target as HTMLSelectElement).value as 'all' | 'yes' | 'no')}
                      >
                        <option value="all">All</option>
                        <option value="yes">Checked</option>
                        <option value="no">Not Checked</option>
                      </select>
                    </div>

                    <!-- Filter Actions -->
                    <div class="col-start-6 flex justify-end mt-4">
                      <button class="btn btn-sm btn-outline btn-error mr-2" @click=${clearFilters}>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              `
            : nothing}

          <div class="overflow-x-auto">
            ${guard([conversationDataviewOptions], () =>
              DataTable({
                options: conversationDataviewOptions.dataViewOptions,
              }),
            )}
          </div>
        </div>
      </div>
    </div>
  `;
});
